import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { X, Minimize2, Send, Brain, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NerdAIChatPopup = ({ isOpen, onClose, initialQuery, examName }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const initialQuerySentRef = useRef(false); // Track if initial query was sent

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Progress bar effect - runs when loading state changes
  useEffect(() => {
    if (loading) {
      // Start progress
      const startTime = Date.now();
      setLoadingProgress(0);
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(95, Math.floor((elapsed / 15000) * 100));
        setLoadingProgress(progress);
        
        if (progress >= 95) {
          clearInterval(interval);
        }
      }, 100);
      
      return () => clearInterval(interval);
    } else {
      // Loading finished
      setLoadingProgress(100);
      const timeout = setTimeout(() => setLoadingProgress(0), 500);
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  const sendMessage = async (messageText = inputText) => {
    const textToSend = messageText.trim();
    
    console.log('sendMessage called with:', textToSend, 'loading:', loading);
    
    if (!textToSend) {
      console.log('Empty message, returning');
      return;
    }
    
    if (loading) {
      console.log('Already loading, ignoring');
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    console.log('Adding user message to chat');
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const queryTimestamp = Date.now();
      console.log('Sending AI query with STREAMING...');
      
      // Create placeholder AI message for streaming
      const aiMessageId = `ai-${queryTimestamp}`;
      const aiMessage = {
        id: aiMessageId,
        text: '',
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Use STREAMING endpoint with FormData (backend expects Form data, not JSON)
      const formData = new FormData();
      formData.append('query', textToSend);
      
      const response = await fetch(`${API}/ai/query-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get AI response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                toast.error(data.chunk);
                accumulatedText = data.chunk;
                break;
              }
              
              if (data.chunk) {
                accumulatedText += data.chunk;
                
                // Update the AI message with accumulated text (REAL STREAMING)
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMessageId 
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));
              }
              
              if (data.done) {
                // Deduct credits after successful AI response (exam practice costs 3 credits)
                try {
                  const token = localStorage.getItem('token');
                  await fetch(`${API}/credits/deduct`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ action: 'ask_nerd_exam' })
                  });
                  // Trigger live credit update
                  window.dispatchEvent(new Event('creditUpdated'));
                } catch (creditError) {
                  console.error('Credit deduction failed:', creditError);
                  // Don't show error to user - silent deduction
                }
                break;
              }
            } catch (e) {
              // Ignore JSON parse errors from incomplete chunks
            }
          }
        }
      }

    } catch (error) {
      console.error('AI query error:', error);
      const errorMessage = error.message || 'Failed to get response';
      toast.error(errorMessage);
      
      // Add error message to chat
      const errorMsg = {
        id: `error-${Date.now()}`,
        text: `Sorry, I encountered an error: ${errorMessage}`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  // Send initial query when popup opens (only runs once per mount due to key prop)
  useEffect(() => {
    if (isOpen && initialQuery && !initialQuerySentRef.current) {
      console.log('Popup mounted with query:', initialQuery);
      initialQuerySentRef.current = true; // Mark as sent
      
      // Add user message immediately
      const userMessage = {
        id: Date.now(),
        text: initialQuery,
        sender: 'user',
        timestamp: new Date()
      };
      setMessages([userMessage]);
      setLoading(true);
      
      // Send to backend (using a separate function to avoid double-calling)
      sendInitialQuery(initialQuery);
    }
    
    // Reset ref when popup closes
    if (!isOpen) {
      initialQuerySentRef.current = false;
    }
  }, [isOpen, initialQuery]);

  const sendInitialQuery = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const queryTimestamp = Date.now();
      console.log(`[${queryTimestamp}] Sending initial AI query to backend with STREAMING...`);
      
      // Create placeholder AI message for streaming
      const aiMessageId = `ai-${queryTimestamp}`;
      const aiMessage = {
        id: aiMessageId,
        text: '',
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Use STREAMING endpoint with FormData (backend expects Form data, not JSON)
      const formData = new FormData();
      formData.append('query', query);
      
      const response = await fetch(`${API}/ai/query-stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get AI response');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                toast.error(data.chunk);
                accumulatedText = data.chunk;
                break;
              }
              
              if (data.chunk) {
                accumulatedText += data.chunk;
                
                // Update the AI message with accumulated text (REAL STREAMING)
                setMessages(prev => prev.map(msg => 
                  msg.id === aiMessageId 
                    ? { ...msg, text: accumulatedText }
                    : msg
                ));
              }
              
              if (data.done) {
                // Deduct credits after successful AI response (exam practice costs 3 credits)
                try {
                  const token = localStorage.getItem('token');
                  await fetch(`${API}/credits/deduct`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ action: 'ask_nerd_exam' })
                  });
                  // Trigger live credit update
                  window.dispatchEvent(new Event('creditUpdated'));
                } catch (creditError) {
                  console.error('Credit deduction failed:', creditError);
                  // Don't show error to user - silent deduction
                }
                break;
              }
            } catch (e) {
              // Ignore JSON parse errors from incomplete chunks
            }
          }
        }
      }

      console.log(`[${queryTimestamp}] Streaming completed`);

    } catch (error) {
      console.error('AI query error:', error);
      
      const errorMessage = error.message || 'Failed to get response';
      toast.error(errorMessage);
      
      const errorMsg = {
        id: `error-${Date.now()}`,
        text: `Sorry, I encountered an error: ${errorMessage}`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-shadow">
          <Brain className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 sm:bottom-6 right-0 sm:right-6 z-50 w-full sm:w-96 sm:max-w-[calc(100vw-3rem)] shadow-2xl">
      <Card className="border-2 border-purple-300 rounded-none sm:rounded-xl">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3 sm:p-4 cursor-move rounded-t-none sm:rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              <CardTitle className="text-base font-semibold">
                Ask Nerd AI !
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={onClose}
                className="hover:bg-white/20 p-1 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {examName && (
            <p className="text-xs text-purple-100 mt-1">Helping with {examName}</p>
          )}
        </CardHeader>
        
        <CardContent className="p-0">
          {/* Messages Area */}
          <div className="h-[60vh] sm:h-96 overflow-y-auto p-3 sm:p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Brain className="h-12 w-12 text-purple-400 mb-3" />
                <p className="text-gray-500 text-sm">
                  Ask me anything about this question!
                </p>
              </div>
            )}
            
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  {message.sender === 'ai' ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  )}
                  <p className={`text-xs mt-1 ${
                    message.sender === 'user' ? 'text-purple-100' : 'text-gray-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-lg p-4 w-full max-w-xs">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-purple-800">Analyzing...</p>
                      <span className="text-sm font-bold text-purple-600">{loadingProgress}%</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-purple-600 to-pink-600 h-full rounded-full transition-all duration-100 ease-linear"
                        style={{ width: `${loadingProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-purple-600">Your answer is being prepared</p>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input Area */}
          <div className="p-3 border-t bg-white">
            <div className="flex gap-2">
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask a follow-up question..."
                className="resize-none text-sm"
                rows={2}
                disabled={loading}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputText.trim() || loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 disabled:opacity-50"
                size="sm"
              >
                {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            {loading && (
              <p className="text-xs text-purple-600 mt-1 font-semibold">Processing... {loadingProgress}%</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NerdAIChatPopup;
